<template>
  <div>
    <!-- ── Phase 1 : chargement en cours ────────────────────────────────────
         Tant que isLoading=true, le <slot> n'est PAS rendu, même si
         showLoadingOverlay est encore false (délai anti-flash de 250ms).
         Cela évite que ParcimmoList se monte sur un IndexedDB vide. -->
    <div v-if="isLoading" data-component="InitialLoading">
      <div v-if="showLoadingOverlay" class="content">
        <div id="logo">
          <Logo />
        </div>
        <h1 class="is-blinking">{{ currentStep }}</h1>
      </div>
    </div>

    <!-- ── Erreur d'initialisation (API inaccessible + IndexedDB vide) ──── -->
    <InitializationError v-else-if="error" />

    <!-- ── Phase 2 : app chargée ───────────────────────────────────────────
         Le <slot> (AppLayout) est TOUJOURS monté ici.
         L'overlay ZIP est rendu en position:fixed AU-DESSUS du slot,
         sans démonter AppLayout. Ainsi son listener syncProgress reste actif
         et peut recevoir le signal isSyncing:false pour fermer l'overlay. -->
    <template v-else>
      <!-- Overlay ZIP (position:fixed via CSS, AppLayout reste monté dessous) -->
      <div v-if="zipSync && showZipSyncOverlay" data-component="InitialLoading">
        <div class="content">
          <div id="logo">
            <Logo />
          </div>
          <h1 class="is-blinking">{{ zipSyncLabel }}</h1>
          <div class="zip-sync-details">
            <p v-if="zipSyncLastSyncDate" class="sync-meta">
              Dernière sync&nbsp;: {{ formatDate(zipSyncLastSyncDate) }}
            </p>
            <p v-if="zipSyncExportDate" class="sync-meta">
              Fichier du&nbsp;: {{ formatDate(zipSyncExportDate) }}
            </p>
            <p v-if="zipSyncProgressLine" class="progress-text">
              {{ zipSyncProgressLine }}
            </p>
            <p v-else class="progress-text downloading">
              Téléchargement du fichier en cours…
            </p>
            <div v-if="zipSyncPercentage > 0" class="progress-bar-wrapper">
              <div class="progress-bar" :style="{ width: zipSyncPercentage + '%' }"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Contenu de l'application (AppLayout + vues) -->
      <slot />
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import AppLogo from './Logo.vue'
import InitializationError from './InitializationError.vue'

const ZIP_SYNC_ENTITY_LABELS = {
  properties: 'Mise à jour des propriétés…',
  controls: 'Mise à jour des contrôles…'
}

export default {
  name: 'InitialLoading',
  components: {
    Logo: AppLogo,
    InitializationError
  },
  data() {
    return {
      showLoadingOverlay: false,
      showZipSyncOverlay: false
    }
  },
  created() {
    this.flashTimer = null
    this.zipSyncTimer = null
  },
  computed: {
    ...mapState('initialLoading', [
      'loading', 'steps', 'currentStepIndex', 'error', 'loadedAt',
      'zipSync', 'zipSyncEntity', 'zipSyncExportDate', 'zipSyncLastSyncDate',
      'zipSyncPhase', 'zipSyncDownloadedBytes', 'zipSyncTotalDownloadBytes',
      'controlsLoaded', 'controlsTotal', 'propertiesLoaded', 'propertiesTotal'
    ]),
    ...mapGetters('initialLoading', ['isLoading', 'currentStep']),

    zipSyncLabel() {
      if (!this.zipSyncEntity) return 'Synchronisation en cours…'
      return ZIP_SYNC_ENTITY_LABELS[this.zipSyncEntity] || `Mise à jour de ${this.zipSyncEntity}…`
    },

    zipSyncLoaded() {
      if (this.zipSyncEntity === 'controls') return this.controlsLoaded
      if (this.zipSyncEntity === 'properties') return this.propertiesLoaded
      return 0
    },
    zipSyncTotal() {
      if (this.zipSyncEntity === 'controls') return this.controlsTotal
      if (this.zipSyncEntity === 'properties') return this.propertiesTotal
      return 0
    },

    formattedDownloadedBytes() {
      const bytes = this.zipSyncDownloadedBytes
      if (!bytes) return ''
      const mb = bytes / (1024 * 1024)
      return mb.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' Mo'
    },
    formattedTotalDownloadBytes() {
      const bytes = this.zipSyncTotalDownloadBytes
      if (!bytes) return ''
      const mb = bytes / (1024 * 1024)
      return mb.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' Mo'
    },

    /**
     * Ligne de progression selon la phase :
     * - 'downloading' < 100% : "X Mo / Y Mo — Z%"  ou  "X Mo téléchargés…"
     * - 'downloading' = 100% : "Traitement des données en cours…"
     * - 'saving'             : "12 450 / 45 000 — 28%"
     * - null                 : '' → affiche "Téléchargement du fichier en cours…"
     */
    zipSyncProgressLine() {
      if (this.zipSyncPhase === 'downloading') {
        const downloaded = this.formattedDownloadedBytes
        if (!downloaded) return ''
        const total = this.formattedTotalDownloadBytes
        if (total) {
          const pct = Math.round((this.zipSyncDownloadedBytes / this.zipSyncTotalDownloadBytes) * 100)
          if (pct >= 100) return 'Traitement des données en cours…'
          return `${downloaded} / ${total} — ${pct}%`
        }
        return `${downloaded} téléchargés…`
      }

      if (this.zipSyncPhase === 'saving') {
        const total = this.zipSyncTotal
        const loaded = this.zipSyncLoaded
        if (total > 0) {
          const percentage = Math.round((loaded / total) * 100)
          return `${loaded.toLocaleString('fr-FR')} / ${total.toLocaleString('fr-FR')} — ${percentage}%`
        }
        return ''
      }

      // Phase inconnue (init ou null) : enregistrements si disponibles
      const total = this.zipSyncTotal
      const loaded = this.zipSyncLoaded
      if (total > 0) {
        const percentage = Math.round((loaded / total) * 100)
        return `${loaded.toLocaleString('fr-FR')} / ${total.toLocaleString('fr-FR')} — ${percentage}%`
      }
      return ''
    },

    zipSyncPercentage() {
      if (this.zipSyncPhase === 'downloading' && this.zipSyncTotalDownloadBytes > 0) {
        const pct = Math.round((this.zipSyncDownloadedBytes / this.zipSyncTotalDownloadBytes) * 100)
        // Bloquer à 99% : le 100% affiche "Traitement…" → pas de barre pleine
        return Math.min(pct, 99)
      }
      const total = this.zipSyncTotal
      const loaded = this.zipSyncLoaded
      if (total > 0) return Math.round((loaded / total) * 100)
      return 0
    }
  },
  watch: {
    zipSync(newVal) {
      if (newVal) {
        this.zipSyncTimer = setTimeout(() => {
          this.showZipSyncOverlay = true
        }, 500)
      } else {
        if (this.zipSyncTimer) {
          clearTimeout(this.zipSyncTimer)
          this.zipSyncTimer = null
        }
        this.showZipSyncOverlay = false
      }
    }
  },
  mounted() {
    if (this.loadedAt) {
      return
    }
    this.flashTimer = setTimeout(() => {
      this.showLoadingOverlay = true
    }, 250)
    this.$store.dispatch('initialLoading/checkLocalStorage')
  },
  beforeUnmount() {
    if (this.flashTimer) clearTimeout(this.flashTimer)
    if (this.zipSyncTimer) clearTimeout(this.zipSyncTimer)
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return ''
      try {
        const normalized = dateString.includes('T') ? dateString : dateString.replace(' ', 'T')
        const date = new Date(normalized)
        if (isNaN(date.getTime())) return dateString
        return date.toLocaleString('fr-FR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        })
      } catch (e) {
        return dateString
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import './InitialLoading.scss';
</style>
